import { Modal, Form, Input, Select } from "antd";
import { Loader2 } from "lucide-react";

interface InvoiceSendEmailProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;

  form: any;
  isSending: boolean;
  error: string | null;
  validateEmail: any;

  invoiceNumber: string;
  senderEmail: string | null;
}

export function InvoiceSendEmail({
  open,
  onClose,
  onSubmit,
  form,
  isSending,
  error,
  validateEmail,
  invoiceNumber,
  senderEmail,
}: InvoiceSendEmailProps) {
  const modalTitle = `Send Invoice #${invoiceNumber}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      footer={
        <div className="flex justify-end gap-2 mt-6">
          <button
            key="cancel"
            onClick={onClose}
            className="btn btn-gray"
            disabled={isSending}
          >
            Cancel
          </button>

          <button
            key="submit"
            type="button"
            className="btn btn-green"
            disabled={isSending || !senderEmail}
            onClick={() => form.submit()}
          >
            {isSending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              "Send Invoice"
            )}
          </button>
        </div>
      }
    >
      <h3 className="text-lg font-semibold mb-4 text-gray-700">{modalTitle}</h3>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Form
        form={form}
        layout="horizontal"
        onFinish={onSubmit}
        className="email-composer-form"
        labelCol={{ span: 3 }}
        wrapperCol={{ span: 21 }}
      >
        {/* From (display only) */}
        <Form.Item label="From" className={`mb-2 ${!senderEmail ? "opacity-80" : ""}`}>
          {senderEmail ? (
            <div className="font-medium">{senderEmail}</div>
          ) : (
            <div className="text-sm text-gray-500">
              No billing email configured. Please configure it in{" "}
              <a
                href="/billing/settings/company-info"
                className="underline text-blue-600"
              >
                Billing → Settings → Company Information
              </a>
            </div>
          )}
        </Form.Item>

        {/* To */}
        <Form.Item
          label="To"
          name="to"
          className="email-composer-separator mb-2"
          rules={[
            {
              required: true,
              message: "Please enter at least one recipient email.",
            },
            { validator: validateEmail },
          ]}
        >
          <Select
            mode="tags"
            placeholder="Type recipient emails and press Enter"
            tokenSeparators={[",", " "]}
            options={[]}
            className="select-no-border"
          />
        </Form.Item>

        {/* Subject */}
        <Form.Item
          label="Subject"
          name="subject"
          className="email-composer-separator mb-4"
          rules={[{ required: true, message: "Subject is required." }]}
        >
          <Input placeholder={"Write your subject..."} bordered={false} />
        </Form.Item>

        {/* Body */}
        <Form.Item label="Message" name="body">
          <Input.TextArea
            rows={8}
            placeholder="Write your message..."
            style={{ resize: "none" }}
          />
        </Form.Item>

        {/* Attachment */}
        <div className="mt-4 p-2 bg-gray-100 border border-dashed border-gray-300 rounded text-sm text-gray-500">
          <span className="font-semibold text-gray-700">Attachment: </span>
          Invoice-{invoiceNumber}.pdf
        </div>
      </Form>
    </Modal>
  );
}
